import { ApiTradingAccount, ApiTransferOfUnits } from 'api/rtk-api'

export interface TradeTransferUnits {
  id: string
  name: string
  transferUnits: (ApiTransferOfUnits & { assetName: string })[]
}

const setupGridDataForAsset = (assetId: string, trades: Map<string, TradeTransferUnits>) => {
  const cells: { id: string; isEmpty: boolean; transferUnits?: ApiTransferOfUnits }[] = []
  const priceFieldIds: string[] = []
  const unitFieldIds: string[] = []
  const defaultUnitFieldValues: { numerical?: number }[] = []
  trades.forEach(trade => {
    const transferUnits = trade.transferUnits.find(x => x.assetId === assetId)
    if (!transferUnits) {
      cells.push({ isEmpty: true, id: trade.id })
      return
    }

    cells.push({ isEmpty: false, id: trade.id, transferUnits })
    const idPrefix = `${trade.id}-${assetId}`
    priceFieldIds.push(`${idPrefix}-price`)
    unitFieldIds.push(`${idPrefix}-unit`)
    defaultUnitFieldValues.push({ numerical: transferUnits.units })
  })

  return {
    cells,
    defaultUnitFieldValues,
    allFieldIds: [...unitFieldIds, ...priceFieldIds],
    priceFieldIds,
    unitFieldIds
  }
}

export type AssetGridData = ReturnType<typeof setupGridDataForAsset> & {
  assetId: string
  assetName: string
}

export const mapData = (tradingAccounts: ApiTradingAccount[]) => {
  const trades = new Map<string, TradeTransferUnits>()
  tradingAccounts.forEach(account => {
    const { pendingAccountState, status } = account
    if (!pendingAccountState?.transferUnits || status !== 'PendingSettlement') return
    trades.set(account.accountId, {
      id: account.accountId,
      name: account.accountName,
      transferUnits: pendingAccountState.transferUnits.map(transferUnit => {
        const { targetWeights } = pendingAccountState.portfolio
        const target = targetWeights.find(target => target.asset.assetId === transferUnit.assetId)
        return {
          ...transferUnit,
          assetName: target?.asset.name ?? transferUnit.assetId
        }
      })
    })
  })
  const assetIds = new Map(
    [...trades.values()].flatMap(trade =>
      trade.transferUnits.map(({ assetId, assetName }) => [assetId, { assetId, assetName }])
    )
  )
  const assets = new Map<string, AssetGridData>()
  assetIds.forEach((value, key) => {
    const data = setupGridDataForAsset(key, trades)
    assets.set(value.assetId, { ...value, ...data })
  })

  return { assets, trades }
}
